import Container from '@/components/Container';

import DefaultContentTemplateTextEditor from '@/components/DefaultContentTemplate/TextEditor';
import Button from '@/components/Button';
import LessonCard from '@/components/Learn/Lesson';
import DefaultContentTemplateAuthorDetailed from '@/components/DefaultContentTemplate/AuthorDetailed';

import { fetchCourseBySlug, fetchRelatedContent, getPage, normalizeLearnItems } from '@/services/learn/course.service';
import { useEffect, useState } from 'react';
import {
  Enum_Course_Type,
  type ComponentCommonAuthorDetailed,
  type Course,
  type CoursePage as CoursePageEntity,
  type Learn,
  GENERATED,
} from '@edwin-group/cj-graphql';
import { useNavigate, useParams } from 'react-router-dom';
import CourseSkeleton from './Course.skeleton';
import { fetchCourseLearn } from '@/services/learn/learn.service';
import { getAuthors, getStartLesson } from '@/utils/learn';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import Navigation, { NavigationSkeleton } from '@/components/ui/navigation';
import { navigationCourse } from '../constants';
import Breadcrumb from '@/components/ui/breadcrumb';
import AnimatedFadeInComponent from '@/components/AnimatedFadeInComponent';

import Highlight from './highlight';
import Divider from '@/components/Divider';
import { ComponentCommonTextEditorWithHtml } from '@/components/DefaultContentTemplate';
import CourseCard from '@/components/Card/Course';
import { trackGAEventOnceExpiresIn5Minutes } from '@/lib/ga-custom-events/dispatcher';
const playIcon = '/card/play-icon.png';

function CoursePage() {
  const [cx, setCourse] = useState<Course | null>(null);
  const [learn, setLearn] = useState<Learn | null>(null);
  const [relatedContent, setRelatedContent] = useState<Array<Course> | null>(null);
  const [page, setPage] = useState<CoursePageEntity | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const { slug } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);

      const [courseResult, relatedContentResult, learnResult, pageReult] = await Promise.all([
        fetchCourseBySlug(slug),
        fetchRelatedContent(slug),
        fetchCourseLearn(slug),
        getPage(),
      ]);

      setCourse(courseResult);
      trackGAEventOnceExpiresIn5Minutes(
        'course_opened',
        {
          course_document_id: courseResult?.documentId ?? '',
          course_name: courseResult?.title ?? courseResult?.attributes?.title ?? '',
        },
        [courseResult?.documentId ?? slug ?? ''],
      );
      setLearn(learnResult as unknown as Learn);
      setPage(pageReult.page);
      setRelatedContent(relatedContentResult);
      setLoading(false);
    })();
  }, [slug]);

  if (loading) {
    return (
      <>
        <NavigationSkeleton />
        <CourseSkeleton />
      </>
    );
  }

  const c = cx?.attributes;
  const normalizedLearnItems = normalizeLearnItems(learn);

  if (c === null) {
    return null;
  }

  const authors = getAuthors(c);
  const startLesson = getStartLesson(c!, learn);
  let startLessonlabel = 'Begin';

  if (learn && learn?.attributes?.completed) {
    startLessonlabel = 'Watch again';
  }

  if (learn && !learn?.attributes?.completed) {
    startLessonlabel = 'Continue';
  }

  const items = [
    {
      label: <>Learn</>,
      url: '/learn',
    },
    {
      label: <>Courses</>,
      url: '/learn',
    },
    { label: c?.title },
  ];

  return (
    <>
      <Navigation title="Learn" items={navigationCourse} />
      <AnimatedFadeInComponent>
        <Breadcrumb items={items} />
      </AnimatedFadeInComponent>
      <Container className="default-content-template min-h-40 !gap-0">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          {learn?.attributes?.completed && c?.courseCompletedSection?.title && c?.courseCompletedSection?.message ? (
            <AnimatedFadeInComponent>
              <div className="mb-sh-30 flex flex-col items-center rounded-2xl border border-sh-blue-light bg-boxes-background p-5">
                <h4 className="flex items-center font-cadiz text-sh-28-32 font-bold">
                  {c?.courseCompletedSection?.title}
                </h4>
                <p className="text-sh-sm">{c?.courseCompletedSection?.message}</p>
              </div>
            </AnimatedFadeInComponent>
          ) : null}
        </div>
        <div className="col-span-12">
          <AnimatedFadeInComponent>
            <Highlight
              cx={cx}
              category={
                c?.type === Enum_Course_Type.Training
                  ? GENERATED.Enum_Bookmark_Category.Training
                  : GENERATED.Enum_Bookmark_Category.Cpd
              }
              video={c?.hero?.data?.attributes?.mime?.includes('video') ? c?.hero?.data?.attributes?.url : ''}
              image={c?.hero?.data?.attributes?.mime?.includes('image') ? c?.hero?.data?.attributes?.url : ''}
              title={c?.title}
              description={c?.description}
              button={
                <>
                  {startLesson ? (
                    <div className="col-span-12 ">
                      <AnimatedFadeInComponent>
                        <Button
                          theme="solid-primary-600"
                          onClick={() =>
                            navigate(
                              `/learn/course/${c?.slug}/module/${startLesson?.idModule}/lesson/${startLesson?.idLesson}`,
                            )
                          }
                          className="col-span-12 !mt-0">
                          {startLessonlabel === 'WATCH AGAIN' ? (
                            <ArrowPathIcon className="inline-block h-4 w-4" />
                          ) : (
                            <img src={playIcon} className="h-auto w-[12px]" alt="play icon" />
                          )}
                          <span>{startLessonlabel}</span>
                        </Button>
                      </AnimatedFadeInComponent>
                    </div>
                  ) : null}
                </>
              }
            />
          </AnimatedFadeInComponent>
        </div>

        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <Divider />
          <div className="flex flex-col gap-sh-15">
            {c?.textContent?.markdownText ? (
              <div className="mb-sh-30">
                <AnimatedFadeInComponent>
                  <DefaultContentTemplateTextEditor
                    component={
                      {
                        id: 'textContent',
                        markdownText: c?.textContent?.markdownText || '',
                        markdownHtml: c?.textContent?.markdownText || '',
                      } as ComponentCommonTextEditorWithHtml
                    }
                    className="!my-0"
                  />
                </AnimatedFadeInComponent>
              </div>
            ) : null}
            <div className="col-span-12 lg:col-start-3 lg:col-end-11">
              {c?.textContent?.markdownText && <Divider className="!mt-0" />}
              <h2 className="mb-[15px] font-cadiz text-sh-36-48 font-bold">{page?.modulesSectionTitle}</h2>
            </div>
            <div className="col-span-12 lg:col-start-3 lg:col-end-11">
              {c?.modules?.map((m, idx) => (
                <AnimatedFadeInComponent key={`${m?.title}-${idx}`}>
                  <div key={`${m?.title}-${idx}`}>
                    <p className="mb-sh-10 font-cadiz text-sh-28-32 font-bold">{m?.title}</p>
                    <p className="mb-6 mt-0 font-cadiz text-sh-16-24">{m?.description}</p>
                    <div className="flex flex-col gap-sh-30">
                      {m?.lessons?.map((lesson) => {
                        const doneLearnItem = !!normalizedLearnItems?.find(
                          (learnItem) =>
                            learnItem?.idModule === m?.moduleStableId && learnItem?.idLesson === lesson?.lessonStableId,
                        );
                        return (
                          <LessonCard
                            title={lesson?.title || ''}
                            description={lesson?.description || ''}
                            thumbnail={lesson!.thumbnail}
                            key={lesson?.lessonStableId}
                            done={doneLearnItem}
                            watchLabel={page?.lesson?.beginButtonLabel || undefined}
                            watchAgainLabel={page?.lesson?.watchAgainButtonlabel || undefined}
                            lockedLabel={page?.lesson?.lockedButtonlabel || undefined}
                            tag="Lesson"
                            duration={lesson?.duration || ''}
                            url={`/learn/course/${c?.slug}/module/${m.moduleStableId}/lesson/${lesson?.lessonStableId}`}
                          />
                        );
                      })}
                    </div>
                    {idx === (c?.modules?.length || 0) - 1 ? null : <Divider />}
                  </div>
                </AnimatedFadeInComponent>
              ))}
              {authors ? (
                <AnimatedFadeInComponent>
                  <>
                    <Divider isBody />
                    <DefaultContentTemplateAuthorDetailed
                      component={
                        {
                          id: 'DefaultContentTemplateAuthorDetailed',
                          authors: {
                            data: authors,
                          },
                        } as unknown as ComponentCommonAuthorDetailed
                      }
                    />
                  </>
                </AnimatedFadeInComponent>
              ) : null}
            </div>
          </div>

          {(relatedContent || []).length > 0 ? (
            <>
              <Divider isBody />
              <div className="mt-sh-60">
                <h3 className="mb-6 text-sh-md font-bold">You might enjoy</h3>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {relatedContent!.map((course) => {
                    return <CourseCard course={course} key={course.documentId} />;
                  })}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </Container>
    </>
  );
}

export default CoursePage;
